import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Music2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { API } from "@/lib/api";
import { PUBLIC_SONG } from "@/constants/testIds";

export default function PublicSong() {
  const { token } = useParams();
  const { t } = useI18n();
  const [song, setSong] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/song/${token}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then(setSong)
      .catch(() => setNotFound(true));
  }, [token]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
        <Music2 className="h-10 w-10 text-muted-foreground" />
        <p className="text-muted-foreground">{t("public.notFound")}</p>
        <Link to="/">
          <Button className="rounded-full">{t("public.createYoursCta")}</Button>
        </Link>
      </div>
    );
  }

  if (!song) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`${song.title} | Kalypso Music`}</title>
        <meta name="description" content={`${song.title} - Une chanson personnalisée créée par intelligence artificielle avec Kalypso Music.`} />
        <meta property="og:title" content={song.title} />
        <meta property="og:description" content={t("public.listenSubtitle")} />
        {song.has_thumbnail && <meta property="og:image" content={`${API}/public/song/${token}/thumbnail`} />}
        <meta property="og:type" content="music.song" />
      </Helmet>
      <main className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center px-4 py-16 text-center">
        <div className="flex items-center gap-2 text-primary">
          <img src="/logo.png" alt="" className="h-6 w-6 rounded-md object-cover" />
          <span className="text-sm font-medium">Kalypso Music</span>
        </div>

        <div className="mt-8 w-full overflow-hidden rounded-3xl border border-border bg-card shadow-xl">
          {song.has_thumbnail && (
            <img src={`${API}/public/song/${token}/thumbnail`} alt={song.title} className="h-72 w-full object-cover" />
          )}
          <div className="p-8">
            <h1 className="font-heading text-2xl font-semibold">{song.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("public.listenSubtitle")}</p>

            {song.has_audio && (
              <audio data-testid={PUBLIC_SONG.player} controls controlsList="nodownload noplaybackrate" onContextMenu={(e) => e.preventDefault()} autoPlay className="mt-6 w-full" src={`${API}/public/song/${token}/audio`} />
            )}

            <Link to="/create" className="mt-8 block">
              <Button className="w-full rounded-full" data-testid={PUBLIC_SONG.createYoursCta}>
                {t("public.createYoursCta")}
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
